import { SHELLBACK_RIDERS } from '../../models/shellback.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * Caparazonte (models/shellback.ts): a hill of the Tortoise's shell on four stumps. It moves like the titan it
 * came from: slowly, every step a shift of the whole dome from one pair of legs to the other (diagonal pairs, as a
 * tortoise walks), the shell rocking a beat behind; the riders on its back cling and sway with it.
 *
 * Conventions: +X on a leg swings its foot back (−Z), +Z on the right legs (+X) splays them out and −Z the left.
 * +X on the body or the head pitches it nose down. Riders pivot at the hold they grip; the shake and die clips
 * fling them off (out and up, tumbling) and shrink them away (they are the sim's to spawn on the ground), and the
 * shake has fresh ones climb back out of the seams at the end (the model can't know how many are left).
 *
 * Blows: attack (it rears and rams forward with the prow) hits at 0.7 s. Shake: it braces (0–0.3 s), shudders
 * from 0.3 s to 1.1 s and the riders let go at 0.55 s (gone by 0.8 s); new ones climb out 1.3–1.8 s.
 */

type B = 'root' | 'body' | 'head' | 'legFL' | 'legFR' | 'legBL' | 'legBR' | 'rider1' | 'rider2' | 'rider3' | 'rider4';
type P = KeyPose<B>;
const RIDERS = ['rider1', 'rider2', 'rider3', 'rider4'] as const;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.5 * k, 1 - k, 1 + 0.5 * k];
};

/** The riders' outward direction on the dome (unit, in the body's frame). */
const OUT = SHELLBACK_RIDERS.map((r) => {
  const l = Math.hypot(r.at[0], r.at[2]) || 1;
  return [r.at[0] / l, r.at[2] / l] as const;
});

/** The riders cling and fidget: small shifts on their own phases. */
const fidget = (b: Bones<B>, t: number, amp: number): void => {
  RIDERS.forEach((n, i) => {
    const w = TAU * (0.45 + i * 0.13) * t + i * 1.7;
    b[n].rot(amp * 0.12 * Math.sin(w), amp * 0.2 * Math.sin(w * 0.7 + 1), amp * 0.1 * Math.cos(w));
  });
};

/**
 * Flung off: each rider is thrown out and up from its hold between `t0` and `t1`, tumbling, shrinking to nothing
 * by `t1` (the sim drops it to the ground). `back` (0–1) has a fresh one climb out of the seams in its place.
 */
const fling = (b: Bones<B>, t: number, t0: number, t1: number, back = 0): void => {
  RIDERS.forEach((n, i) => {
    const d = t0 + i * 0.05;
    const k = span(t, d, t1 + i * 0.05, outQuad);
    const [ox, oz] = OUT[i]!;
    const up = 0.9 * Math.sin(Math.PI * Math.min(1, k * 1.2));
    const gone = span(t, d + (t1 - t0) * 0.55, t1 + i * 0.05, inQuad);
    // A fresh one climbs out where it held: from just under the plates, growing back to full size.
    const climb = back > 0 ? span(back, i * 0.12, 0.64 + i * 0.12, outBack) : 0;
    if (climb > 0) {
      b[n].pos(0, -0.25 * Math.max(0, 1 - climb), 0).scale(Math.max(0.01, climb));
      return;
    }
    b[n].pos(ox * 0.9 * k, up - 0.4 * k * k, oz * 0.9 * k).rot(k * (2.5 + i), k * (1.5 - i), k * (i % 2 ? 2 : -2));
    b[n].scale(Math.max(0.01, 1 - gone));
    // Gone, it waits inside the dome (its eyes' glows follow the bone, not its size).
    if (gone > 0.99) b[n].pos(-ox * 0.9 * k, -(up - 0.4 * k * k) - 0.9, -oz * 0.9 * k);
  });
};

const idle = (): Clip<B> =>
  clip<B>('idle', 4.0, true, (b, t) => {
    const p = phase(t, 4.0) * TAU;
    const br = Math.sin(p);
    b.root.pos(0, 0.012 * br, 0);
    b.body.rot(0.012 * Math.sin(p + 0.5), 0, 0.01 * Math.sin(p * 2 + 0.3)).scale(1 + 0.006 * br);
    // The head pushes out of the prow and draws back, turning.
    b.head.rot(0.06 * Math.sin(p + 1.2), 0.2 * Math.sin(p * 0.5 + 0.4), 0).pos(0, 0, 0.04 * (1 + Math.sin(p + 1.2)));
    fidget(b, t, 1);
  }, (t) => {
    const br = Math.sin(phase(t, 4.0) * TAU);
    return [1 + 0.006 * br, 1 - 0.005 * br, 1 + 0.006 * br];
  });

/** The plod: diagonal pairs (front left with back right), the dome rolling from pair to pair, a thud on each. */
const walk = (dur = 2.8): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const A = 0.32;
    // A pair swings forward (−X) lifted (it shortens: a slight splay) while the other pushes back.
    const lift = (x: number): number => Math.max(0, x) ** 1.5;
    b.legFL.rot(-A * sn, 0, -0.1 * lift(sn));
    b.legBR.rot(-A * sn, 0, 0.1 * lift(sn));
    b.legFR.rot(A * sn, 0, 0.1 * lift(-sn));
    b.legBL.rot(A * sn, 0, -0.1 * lift(-sn));
    // Two thuds a cycle: the dome drops and rolls toward the planted side.
    const d = Math.abs(cs);
    b.root.pos(0, -0.05 * (1 - d) ** 2 + 0.02, 0.02 * Math.sin(2 * th));
    b.body.rot(0.025 * Math.sin(2 * th - 0.6), 0.04 * sn, 0.05 * Math.sin(th - 0.5));
    b.head.rot(0.08 + 0.05 * Math.sin(2 * th - 1), -0.1 * sn, 0).pos(0, 0, 0.06);
    fidget(b, t, 1.3);
    RIDERS.forEach((n, i) => b[n].rot(0.1 * Math.sin(2 * th - 1.2 - i * 0.3), 0, 0.08 * Math.sin(th - 1 - i * 0.2)));
  }, (t) => {
    const k = Math.max(0, Math.cos(2 * phase(t, dur) * TAU));
    return [1 + 0.012 * k, 1 - 0.018 * k, 1 + 0.012 * k];
  });

/** Embestida: it rears, the front legs dig in, then it lunges a whole step with the prow (0.7 s) and shoves. */
const attack = (): Clip<B> => {
  const rear: P = { root: [0, 0, 0, 0, 0.06, -0.2], body: [-0.14, 0, 0], head: [-0.3, 0, 0, 0, 0, -0.1], legFL: [-0.3, 0, -0.08], legFR: [-0.3, 0, 0.08], legBL: [0.25, 0, -0.05], legBR: [0.25, 0, 0.05] };
  const ram: P = { root: [0, 0, 0, 0, -0.06, 0.5], body: [0.14, 0, 0], head: [0.3, 0, 0, 0, 0, 0.16], legFL: [0.15, 0, -0.06], legFR: [0.15, 0, 0.06], legBL: [0.5, 0, -0.04], legBR: [0.5, 0, 0.04] };
  return clip<B>('attack', 1.7, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.45, rear, inOut],
      [0.7, ram, inCubic],
      [0.95, { ...ram, root: [0, 0, 0, 0, -0.05, 0.44], head: [0.24, 0, 0, 0, 0, 0.12] }, outBack],
      [1.7, {}, inOut],
    ], { head: 0.03 });
    b.body.rot(0, 0, 0.04 * kick(t, 0.7, 4, 6));
    // The riders hang on through the rear and are thrown forward by the impact.
    RIDERS.forEach((n, i) => b[n].rot(0.35 * kick(t, 0.72 + i * 0.03, 2.5, 5) - 0.2 * envelope(t, 0.2, 0.45, 0.6, 0.7), 0, 0));
  }, (t) => thud(t, [[0.7, 0.07]]));
};

const hit = (): Clip<B> =>
  clip<B>('hit', 0.6, false, (b, t) => {
    const q = settle(t, 2.2, 8) * span(t, 0, 0.04, outQuad);
    b.root.pos(0, 0, -0.05 * q);
    b.body.rot(-0.05 * q, 0.04 * q, 0.03 * q);
    b.head.rot(-0.2 * q, 0, 0).pos(0, 0, -0.08 * q);
    RIDERS.forEach((n, i) => b[n].rot(-0.25 * q, 0, (i % 2 ? 0.2 : -0.2) * q));
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 3.0, true, (b, t) => {
    const p = phase(t, 3.0) * TAU;
    b.root.pos(0, -0.08, 0).rot(0, 0.06 * Math.sin(p * 0.5), 0);
    b.body.rot(0.06 + 0.03 * Math.sin(p), 0, 0.06 * Math.sin(p + 0.5));
    b.head.rot(0.4 + 0.08 * Math.sin(p + 1), 0.25 * Math.sin(p + 1), 0.15 * Math.sin(p)).pos(0, -0.04, -0.08);
    b.legFL.rot(-0.1, 0, -0.18);
    b.legFR.rot(-0.1, 0, 0.18);
    b.legBL.rot(0.1, 0, -0.14);
    b.legBR.rot(0.1, 0, 0.14);
    fidget(b, t, 1.8);
  });

/**
 * Shake (1.8 s): it braces low, then the whole dome shudders side to side and bucks; the riders lose their hold at
 * 0.55 s and are thrown off, tumbling (gone by 0.8 s); it settles, and new ones crawl out of the seams (1.3–1.8 s).
 */
const shake = (): Clip<B> =>
  clip<B>('shake', 1.8, false, (b, t) => {
    const brace: P = { root: [0, 0, 0, 0, -0.1, 0], body: [0.05, 0, 0], head: [0.25, 0, 0, 0, 0, -0.12], legFL: [-0.1, 0, -0.25], legFR: [-0.1, 0, 0.25], legBL: [0.1, 0, -0.25], legBR: [0.1, 0, 0.25] };
    timeline(b, t, [
      [0, {}],
      [0.28, brace, inOut],
      [1.1, brace],
      [1.5, {}, inOut],
    ], { head: 0.03 });
    const on = envelope(t, 0.28, 0.36, 1.0, 1.15);
    b.body.rot(0.06 * Math.sin(TAU * 5 * t) * on, 0.1 * Math.sin(TAU * 3.5 * t + 1) * on, 0.2 * Math.sin(TAU * 7 * t) * on);
    b.root.pos(0.05 * Math.sin(TAU * 7 * t + 0.5) * on, 0.07 * Math.abs(Math.sin(TAU * 5 * t)) * on, 0);
    b.head.rot(0, 0.25 * Math.sin(TAU * 3.5 * t) * on, 0);
    fling(b, t, 0.4, 0.8, span(t, 1.25, 1.8));
  }, (t) => {
    const on = envelope(t, 0.28, 0.36, 1.0, 1.15);
    const s = Math.sin(TAU * 10 * t) * on;
    return [1 + 0.02 * s, 1 - 0.03 * s, 1 + 0.02 * s];
  }, 'Sacudirse');

/** The fall (2.8 s): the riders leap off, its front legs give (0.7 s), then the back (1.1 s), and the dome crashes down and tips. */
const die = (): Clip<B> =>
  clip<B>('die', 2.8, false, (b, t) => {
    const frontGone: P = { root: [0.12, 0, 0, 0, -0.22, 0.05], body: [0.18, 0, 0.04], head: [0.5, 0, 0, 0, -0.1, -0.05], legFL: [-0.7, 0, -0.9], legFR: [-0.7, 0, 0.9], legBL: [0.1, 0, -0.1], legBR: [0.1, 0, 0.1] };
    const down: P = { root: [0.06, 0.1, 0.14, 0, -0.5, 0.05], body: [0.06, 0, 0.1], head: [0.7, 0.3, 0.2, 0, -0.14, -0.08], legFL: [-0.8, 0, -1.3], legFR: [-0.8, 0, 1.3], legBL: [0.9, 0, -1.2], legBR: [0.9, 0, 1.2] };
    timeline(b, t, [
      [0, {}],
      [0.35, { root: [-0.06, 0, 0, 0, 0.04, 0], body: [-0.1, 0, 0], head: [-0.5, 0, 0, 0, 0.04, 0.06] }, outExpo],
      [0.7, frontGone, inQuad],
      [0.85, { ...frontGone, root: [0.1, 0, 0, 0, -0.2, 0.05] }, outBack],
      [1.1, down, inCubic],
      [1.35, { ...down, root: [0.07, 0.1, 0.15, 0, -0.48, 0.05] }, outBack],
      [2.8, { ...down, head: [0.85, 0.35, 0.25, 0, -0.16, -0.1] }, inOut],
    ], { head: 0.05 });
    fling(b, t, 0.1, 0.6);
  }, (t) => thud(t, [[0.7, 0.05], [1.1, 0.09]]));

/** Clips by library kind (see ../rigs.ts). */
export const shellbackClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), shake()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { shellback: shellbackClips };

